import React, { memo, useMemo, useCallback, useState } from "react"
import { SettingsPageContainer } from "../../../../../components/Settings/SettingsPageContainer"
import { PasswordContainer } from "../../../../../components/PasswordContainer"
import { useLocalization } from "../../../../../hooks/localization"
import { SettingsList, ColumnModel } from "../../../../../components/Settings/SettingsList"
import { Theme } from "react-select"
import { useSelector } from 'react-redux';
import { useMessage } from "../../../../../hooks/message"
import { useRPC } from "../../../../../hooks/rpc"

type RGBColorModel = {
  r: number;
  g: number;
  b: number;
};

type ButtonColorModel = {
  is_enabled?: boolean;
  mode?: string;
  color: RGBColorModel;
  bridge_device_list: string[];
};

type ThemeModel = {
  id: string;
  name: string;
  primary_button_color: ButtonColorModel;
  active_button_color: ButtonColorModel;
};

export const LucentThemeListScreen = memo(() => {
  const { t } = useLocalization()
  const items = useSelector(state => state.control.lucentRGBSettingsList);
  const [selectedItem, setSelectedItem] = useState(undefined);
  const keyExtractor = (item: ThemeModel) => item.id;
  const displayNameExtractor = useCallback((item: ThemeModel, _) => item.name, []);
  const columns = useMemo<ColumnModel[]>(() => [{ length: 6, name: t('lucent_themes'), key: 'name' }], [t]);
  const onItemSelected = (item: Theme) => setSelectedItem(item);
  const [rpc] = useRPC();
  const { success, failure } = useMessage();

  const applyButtonCallback = useCallback(() => {
    if (!selectedItem) { return; }
    rpc.setLucentRGBSettings(selectedItem.id).then(() => {  
      success(t('lucent_color_set_success'));
    }).catch(error => {
      failure(error.message);
      rpc.frontendErrorLog("Lucent color set error:" + error.message)
    });
  }, [selectedItem, rpc, success, failure, t])

  const buttons = useMemo(() => {
    return [
      {
        text: t("apply"),
        onClick: applyButtonCallback,
        className: "blue",
        hidden: !selectedItem
      },
      {
        text: t("edit"),
        className: "green",
        url: `/settings/custom-menu/lucent-themes/edit/${selectedItem ? selectedItem.id : ''}/`,
        hidden: !selectedItem
      },
      {
        text: t("add"),
        className: "yellow",
        url: `/settings/custom-menu/lucent-themes/create/`,
        hidden: false
      }
    ]
  }, [selectedItem, t, applyButtonCallback])

  return (
    <PasswordContainer requiredRole="default" isFullscreen={true}>
      <SettingsPageContainer buttons={buttons} backUrl={"/settings/custom-menu"}>
        <SettingsList {...{ items, columns, keyExtractor, displayNameExtractor, selectedItem, onItemSelected }} />
      </SettingsPageContainer>
    </PasswordContainer>
  )
})
